import React, { useState, useReducer, useEffect } from "react";
import {
  Card,
  CardContent,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Tooltip,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import { post } from "aws-amplify/api";
import { I18n } from "@aws-amplify/core";
import useMediaQuery from "@mui/material/useMediaQuery";
import currentUserToken from "CurrentUserToken";
import { APIStatusDialog } from "components/APIStatusDialog";
import { DarkMode } from "@mui/icons-material";

const reducer = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    const audit = action.value;
    if (Array.isArray(audit)) {
      return { audit: audit, fetched: true };
    }
  }
  return lastState;
};

const ContactNote = ({ data, profile, tlmGetAccoutsCountryCode }) => {
  const isMobileDevice = useMediaQuery("(max-width:600px)");
  const [state, dispatch] = useReducer(reducer, {
    audit: [],
    fetched: false,
  });
  const [apiStatus, setApiStatus] = useState({
    open: false,
    status: "",
    message: "",
  });
  const [isEditable, setIsEditable] = useState(false);
  const [notes, setNotes] = useState({
    secondary_note: "",
    shift: "",
  });

  useEffect(() => {
    setNotes({
      secondary_note: data.secondary_note || "",
      shift: data.shift || "",
    });
  }, [data]);

  const handleEditClick = () => setIsEditable(true);

  const handleCancelClick = () => {
    setIsEditable(false);
    setNotes({
      secondary_note: data.secondary_note || "",
      shift: data.shift || "",
    });
  };

  const handleSaveClick = async () => {
    try {
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Saving notes"),
      });
      const restOperation = post({
        apiName: "TelemonitoringAPI",
        path: "/accountNumber/note/post",
        options: {
          body: {
            secondary_note: notes.secondary_note,
            shift: notes.shift,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            account_number: data.account_number,
          },
        },
      });
      const response = await restOperation.response;
      if (response.statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("Notes successfully saved"),
        });
        tlmGetAccoutsCountryCode();
      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Failed to save notes"),
        });
      }
      setIsEditable(false);
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Failed to save notes"),
      });
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    }
  };

  const handleNoteChange = (event) => {
    const { name, value } = event.target;
    setNotes((prev) => ({ ...prev, [name]: value }));
  };

  const headerCellsNote = [
    { id: "secondary_note", label: I18n.get("Contact Note") },
    { id: "shift", label: I18n.get("Shift") },
  ];

  const hasProfilePermission =
    profile && (profile.includes("SuperAdmin") || profile.includes("Admin"));
  const canEdit = hasProfilePermission;
  const shiftItem = [ "A", "B", "C", "D", "E", "F", "G"];
  return (
    <Card sx={{ width: "100%", marginBottom: "1rem", marginTop: "1rem" }}>
      <CardContent sx={{ padding: "1rem" }}>
        <Stack
          spacing={2}
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          // sx={{ pb: 2 }}
        >
          {isEditable ? (
            <>
              <Tooltip title={I18n.get("Save Notes")}>
                <IconButton onClick={handleSaveClick}>
                  <SaveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={I18n.get("Cancel Edit")}>
                <IconButton onClick={handleCancelClick}>
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip
              title={
                canEdit
                  ? I18n.get("Edit Notes")
                  : "Edit Disabled - Insufficient Permissions"
              }
            >
              <span>
                <IconButton
                  onClick={canEdit ? handleEditClick : null}
                  disabled={!canEdit}
                >
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
        <TableContainer sx={{ width: "100%" }}>
          <Table sx={{ width: "100%" }}>
            <TableBody>
              {headerCellsNote.map((column, colIndex) => (
                <TableRow key={colIndex}>
                  <TableCell sx={{ padding: "0rem" }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {column.label}:
                    </Typography>
                    {isEditable ? (
                      column.id === "shift" ? (
                        <Select
                          name="shift"
                          value={notes.shift}
                          onChange={handleNoteChange}
                          sx={{
                            "& .MuiOutlinedInput-input":{
                              paddingLeft:"7px",
                              paddingTop:"2px",
                              paddingBottom:"2px",
                            },
                            fontSize:"16px"}}
                        >
                          {shiftItem.map((value) => (
                            <MenuItem key={value} value={value}>
                              {value}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <TextField
                          name={column.id}
                          value={notes[column.id]}
                          onChange={handleNoteChange}
                          multiline
                          minRows={1}
                          maxRows={5}
                          sx={{
                            "& .MuiInputBase-input":{
                              padding:"0px"
                            },
                            width: "100%",
                            border:"none",
                            padding: "4px",
                            fontSize: "16px",
                            background:
                              localStorage.getItem("darkMode") === "true"
                                ? "#1f2020"
                                : "",
                            color:localStorage.getItem("darkMode")=== "true"?"#d2d2d2":"#1f2020" 
                          }}
                        />
                      )
                    ) : ( //se non è editabile viene mostrato il valore normalmente
                      <Typography variant="body2">
                        {notes[column.id] || ""}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
      <APIStatusDialog
        open={apiStatus.open}
        status={apiStatus.status}
        message={apiStatus.message}
      />
    </Card>
  );
};

export default ContactNote;
